import { createSlice } from "@reduxjs/toolkit";
import {
  createEnrollment,
  updateEnrollment,
  getEnrollmentById,
  createEnrollmentLoad,
  updateEnrollmentLoad,
  getEnrollmentByIdLoad,
  getTerminalById,
  updateTerminal,
} from "./MerchantAndTerminals.thunk";

const baseState = {
  // Form data
  formData: null,
  initialFormData: null,

  // Form state
  activeStep: 0,
  filteredSteps: [],
  submittedSteps: [], // Changed from Set to Array for serialization
  invalidFields: [],
  loading: false,

  // Form modes
  formType: "createEnrollment", // 'createEnrollment' | 'loadEnrollment' | 'editTerminal' | 'seeTerminal'
  readOnlyForm: false,

  // Enrollment data
  enrollmentDataById: null,
  enrollmentSelected: null,
  enrollmentId: null, // ID for loadEnrollment and editTerminal modes

  // Terminal data
  terminalData: null,
  terminalItemInfo: null, // ID for editTerminal and seeTerminal modes

  // Status states
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  statusRetrieve: "idle",
  statusCreate: "idle",
  statusUpdate: "idle",
  statusLoadCreate: "idle",
  statusLoadUpdate: "idle",
  statusTerminalRetrieve: "idle",

  // Error handling
  error: null,

  // File upload
  file: null,
  successfulData: [],
  failedData: [],
  isModalOpen: false,
};

const initialState = {
  ...baseState,
};

export const merchantAndTerminalsSlice = createSlice({
  name: "merchantAndTerminals",
  initialState,
  reducers: {
    // Reset actions
    ResetStore: (state) => {
      return { ...baseState };
    },

    ResetFormData: (state) => {
      state.formData = state.initialFormData;
      state.activeStep = 0;
      state.submittedSteps = new Set();
      state.invalidFields = [];
      state.loading = false;
      state.error = null;
      state.terminalData = null;
      state.terminalItemInfo = null;
    },

    // Form state actions
    setFormData: (state, action) => {
      state.formData = action.payload;
    },

    setActiveStep: (state, action) => {
      state.activeStep = action.payload;
    },

    setFilteredSteps: (state, action) => {
      state.filteredSteps = action.payload;
    },

    setSubmittedSteps: (state, action) => {
      // Ensure we always store an array
      state.submittedSteps = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];
    },

    setInvalidFields: (state, action) => {
      state.invalidFields = action.payload;
    },

    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Form mode actions
    setFormType: (state, action) => {
      state.formType = action.payload;
    },

    setReadOnlyForm: (state, action) => {
      state.readOnlyForm = action.payload;
    },

    // Form configuration actions
    configureForm: (state, action) => {
      const {
        formType,
        enrollmentId,
        initialData,
        readOnly,
        terminalData,
        terminalItemInfo,
        readOnlyForm,
      } = action.payload;
      state.formType = formType || "createEnrollment";
      state.enrollmentId = enrollmentId || null;
      state.initialFormData = initialData || null;
      state.readOnlyForm = readOnly || false;
      state.terminalData = terminalData || null;
      state.terminalItemInfo = terminalItemInfo || null;
      state.readOnlyForm = readOnlyForm || false;

      // Reset form state when configuring
      state.activeStep = 0;
      state.submittedSteps = [];
      state.invalidFields = [];
      state.error = null;
    },

    // Enrollment actions
    setEnrollmentSelected: (state, action) => {
      state.enrollmentSelected = action.payload;
    },

    setEnrollmentId: (state, action) => {
      state.enrollmentId = action.payload;
    },

    resetEnrollmentData: (state) => {
      state.enrollmentDataById = null;
      state.statusRetrieve = "idle";
    },

    // File upload actions
    setFile: (state, action) => {
      state.file = action.payload;
    },

    setSuccessfulData: (state, action) => {
      state.successfulData = action.payload;
    },

    setFailedData: (state, action) => {
      state.failedData = action.payload;
    },

    setIsModalOpen: (state, action) => {
      state.isModalOpen = action.payload;
    },

    // Error actions
    setError: (state, action) => {
      state.error = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    setTerminalData: (state, action) => {
      state.terminalData = action.payload;
    },

    setTerminalItemInfo: (state, action) => {
      const { formType, terminalItemInfo, readOnlyForm } = action.payload;
      state.formType = formType || "editTerminal";
      state.terminalItemInfo = terminalItemInfo;
      state.readOnlyForm = readOnlyForm;
      state.statusTerminalRetrieve = "idle";
      state.terminalData = null;
    },
  },
  extraReducers: (builder) => {
    // Create enrollment
    builder
      .addCase(createEnrollment.pending, (state) => {
        state.statusCreate = "loading";
        state.loading = true;
      })
      .addCase(createEnrollment.fulfilled, (state, action) => {
        state.statusCreate = "succeeded";
        state.loading = false;
        state.error = null;
      })
      .addCase(createEnrollment.rejected, (state, action) => {
        state.statusCreate = "failed";
        state.loading = false;
        state.error = action.payload?.message || "Error creating enrollment";
      });

    // Update enrollment
    builder
      .addCase(updateEnrollment.pending, (state) => {
        state.statusUpdate = "loading";
        state.loading = true;
      })
      .addCase(updateEnrollment.fulfilled, (state, action) => {
        state.statusUpdate = "succeeded";
        state.loading = false;
        state.error = null;
      })
      .addCase(updateEnrollment.rejected, (state, action) => {
        state.statusUpdate = "failed";
        state.loading = false;
        state.error = action.payload?.message || "Error updating enrollment";
      });

    // Get enrollment by ID
    builder
      .addCase(getEnrollmentById.pending, (state) => {
        state.statusRetrieve = "loading";
        state.loading = true;
      })
      .addCase(getEnrollmentById.fulfilled, (state, action) => {
        const { enrollmentData } = action.payload;
        state.statusRetrieve = "succeeded";
        state.loading = false;
        state.enrollmentDataById = enrollmentData;
        state.error = null;
      })
      .addCase(getEnrollmentById.rejected, (state, action) => {
        state.statusRetrieve = "failed";
        state.loading = false;
        state.error = action.payload?.message || "Error retrieving enrollment";
      });

    // Create enrollment load
    builder
      .addCase(createEnrollmentLoad.pending, (state) => {
        state.statusLoadCreate = "loading";
        state.loading = true;
      })
      .addCase(createEnrollmentLoad.fulfilled, (state, action) => {
        const { enrollmentCreated } = action.payload;
        state.statusLoadCreate = "succeeded";
        state.loading = false;
        state.enrollmentDataById = enrollmentCreated;
        state.error = null;
      })
      .addCase(createEnrollmentLoad.rejected, (state, action) => {
        state.statusLoadCreate = "failed";
        state.loading = false;
        state.error =
          action.payload?.message || "Error creating enrollment load";
      });

    // Update enrollment load
    builder
      .addCase(updateEnrollmentLoad.pending, (state) => {
        state.statusLoadUpdate = "loading";
        state.loading = true;
      })
      .addCase(updateEnrollmentLoad.fulfilled, (state, action) => {
        const { enrollmentUpdated } = action.payload;
        state.statusLoadUpdate = "succeeded";
        state.loading = false;
        state.enrollmentDataById = enrollmentUpdated;
        state.error = null;
      })
      .addCase(updateEnrollmentLoad.rejected, (state, action) => {
        state.statusLoadUpdate = "failed";
        state.loading = false;
        state.error =
          action.payload?.message || "Error updating enrollment load";
      });

    // Get enrollment by ID load
    builder
      .addCase(getEnrollmentByIdLoad.pending, (state) => {
        state.statusRetrieve = "loading";
        state.loading = true;
      })
      .addCase(getEnrollmentByIdLoad.fulfilled, (state, action) => {
        const { enrollmentFilterdById } = action.payload;
        state.statusRetrieve = "succeeded";
        state.loading = false;
        state.enrollmentDataById = enrollmentFilterdById;
        state.error = null;
      })
      .addCase(getEnrollmentByIdLoad.rejected, (state, action) => {
        state.statusRetrieve = "failed";
        state.loading = false;
        state.error =
          action.payload?.message || "Error retrieving enrollment load";
      });

    // Get terminal by ID
    builder
      .addCase(getTerminalById.pending, (state) => {
        state.statusTerminalRetrieve = "loading";
        state.loading = true;
      })
      .addCase(getTerminalById.fulfilled, (state, action) => {
        const { terminalData } = action.payload;
        state.statusTerminalRetrieve = "succeeded";
        state.loading = false;
        state.terminalData = terminalData;
        state.error = null;
      })
      .addCase(getTerminalById.rejected, (state, action) => {
        state.statusTerminalRetrieve = "failed";
        state.loading = false;
        state.error =
          action.payload?.message || "Error retrieving enrollment load";
      });

    // Update terminal
    builder
      .addCase(updateTerminal.pending, (state) => {
        state.statusTerminalRetrieve = "loading";
        state.loading = true;
      })
      .addCase(updateTerminal.fulfilled, (state, action) => {
        const { terminalData2 } = action.payload;
        state.statusTerminalRetrieve = "succeeded";
        state.loading = false;
        state.terminalData2 = terminalData2;
        state.error = null;
      })
      .addCase(updateTerminal.rejected, (state, action) => {
        state.statusTerminalRetrieve = "failed";
        state.loading = false;
        state.error =
          action.payload?.message || "Error retrieving enrollment load";
      });
  },
});

export const {
  ResetStore,
  ResetFormData,
  setFormData,
  setActiveStep,
  setFilteredSteps,
  setSubmittedSteps,
  setInvalidFields,
  setLoading,
  setFormType,
  setReadOnlyForm,
  configureForm,
  setEnrollmentSelected,
  setEnrollmentId,
  resetEnrollmentData,
  setFile,
  setSuccessfulData,
  setFailedData,
  setIsModalOpen,
  setError,
  clearError,
  setTerminalItemInfo,
} = merchantAndTerminalsSlice.actions;

export default merchantAndTerminalsSlice.reducer;
